import { useRecentWorkspaceFlowSessions } from "@/api/flow_sessions";
import { Accordion } from "@/components/ui/accordion";
import { useActiveChatFlowSessionId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useActiveChatFlowSessionId";
import { useChatsRailActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useChatsRailActions";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { WorkspacesRailChatNode } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatNode";
import type { InboxItem } from "@cohesive/api-client";
import { MessagesSquare, Pin } from "lucide-react";
import { useMemo } from "react";

/**
 * The all-chats rail view: one chronological stream (chats read like a
 * history, not an org chart — the workspace pages keep the foldered views),
 * with a Pinned group on top. Creation lives in the rail head's chat verb.
 * Chats are PINNED rather than favourited — you pin a conversation to keep it
 * to hand; favouriting one reads oddly, and the rest of the app keeps the
 * favourite vocabulary for canvases, files and skills.
 */
export function ChatsAllRail() {
  const { data: recentChats = [] } = useRecentWorkspaceFlowSessions(50);
  const activeFlowSessionId = useActiveChatFlowSessionId();
  const { onOpen, onToggleStar, onRename, onDelete } = useChatsRailActions();

  const starred = useMemo(
    () => recentChats.filter((c) => c.is_starred),
    [recentChats],
  );

  const renderChat = (chat: InboxItem, ns: string) => (
    <WorkspacesRailChatNode
      key={chat.id}
      item={chat}
      dragNamespace={ns}
      active={activeFlowSessionId === chat.flow_session_id}
      onOpen={onOpen}
      onToggleStar={onToggleStar}
      onRename={onRename}
      onDelete={onDelete}
    />
  );

  return (
    // Padding belongs to the scroller inside, never to the flex item itself:
    // a border-box element can't shrink below its own padding, which would
    // floor this rail and stop the drawer short of where the other rails
    // let it reach.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2 pt-3">
        <Accordion
          type="multiple"
          defaultValue={["chats-all"]}
          className="flex flex-col gap-2"
        >
          {starred.length > 0 && (
            <RailGroup
              groupId="chats-starred"
              label="Pinned"
              icon={Pin}
              noCap
              items={starred.map((chat) =>
                renderChat(chat, "chats-all:starred"),
              )}
            />
          )}
          <RailGroup
            groupId="chats-all"
            label="Chats"
            icon={MessagesSquare}
            noCap
            items={recentChats.map((chat) => renderChat(chat, "chats-all"))}
          />
        </Accordion>
      </div>
    </div>
  );
}
